import { cn } from '@divlab/divanui';
import { useInView } from 'react-intersection-observer';

import Page from '@Components/Page';
import LayoutMain from '@Layouts/LayoutMain';
import { useRequest } from '@Contexts/Request';
import ShowroomsMap from '@Components/ShowroomsMap';
import useTranslation from '@Queries/useTranslation';
import usePage from '@Queries/usePage';
import PageTopBar from '@Components/PageTopBar';
import GallerySection from '@Components/GallerySection';
import SmartRender from '@Components/SmartRender';
import AdviseAssortmentBanner from './elems/AdviseAssortmentBanner';
import InviteToGetInspired from './elems/InviteToGetInspired/InviteToGetInspired';
import QuestionsBlock from './elems/Questions/QuestionsBlock';
import Img from '@UI/Img';
import styles from './PageShowroom.module.css';

import type { PageShowroomData } from './typings';
import type { FC } from 'react';

const PageShowroom: FC = () => {
  const { secondaryRegion, region } = useRequest();
  const key = region && region === 'other' && secondaryRegion ? secondaryRegion : '';
  const { data: page } = usePage<PageShowroomData>([key]);
  const { t } = useTranslation();
  const { map, final, gallery, banner, info, feedbackForm } = page;
  const hasShowrooms = page.page !== 'ShowroomAll';
  const { inView, ref } = useInView();

  return (
    <Page>
      <LayoutMain unfixHeader={inView}>
        <PageTopBar title={page.meta.h1} breadcrumbs={page.breadcrumbs} />
        <div className={styles.page} data-testid='page-showroom'>
          {map && (
            <div className={styles.container} ref={ref}>
              <ShowroomsMap map={map} />
            </div>
          )}

          <div className={styles.container}>
            {!hasShowrooms && (
              <div className={styles.borderedBlock}>
                <div className={styles.subtitle}>{t('no-showroom.title')}</div>
                <div className={styles.description}>{t('no-showroom.description')}</div>
              </div>
            )}
          </div>

          <GallerySection
            className={styles.section}
            cnGallery={styles.gallery}
            cnSlide={styles.slide}
            cnDescription={styles.blockText}
            defaultGap
            flexible
            title={gallery.title}
            description={gallery.text}
            slides={gallery.images}
            renderSlide={(image) => <Img src={image} />}
          />

          <div className={cn(styles.container, styles.noGapContainer)}>
            <div className={styles.section}>
              <InviteToGetInspired className={styles.inviteToGetInspired} info={info} />
            </div>
          </div>

          <div className={cn(styles.container, styles.bannerContainer)}>
            <div className={styles.section}>
              <AdviseAssortmentBanner banner={banner} />
            </div>
          </div>

          <div className={cn(styles.container, styles.bannerContainer)}>
            <div className={cn(styles.section, styles.textBlock)}>
              <h2 className={styles.blockTitle}>{final.title}</h2>
              <p className={styles.blockText}>{final.text}</p>
            </div>
            <SmartRender countries={['BLR', 'KAZ', 'UZB']}>
              <div className={styles.section}>
                <QuestionsBlock feedbackForm={feedbackForm} />
              </div>
            </SmartRender>
          </div>
        </div>
      </LayoutMain>
    </Page>
  );
};

export default PageShowroom;
